import { useState, useMemo, useEffect } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { resolveNavLink } from '@/utils/navigation'
import {
  hasSeenPageAlertsInSession,
  markPageAlertsSeenInSession,
} from '@/utils/pageAlertsSession'
import { GetAlertsQuery } from '@/gql/graphql'

type AlertType = GetAlertsQuery['getAlerts'][0]

export interface AlertsProps {
  alerts?: AlertType[]
}

export default function Alerts({ alerts }: AlertsProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(() => !hasSeenPageAlertsInSession())
  const [currentIndex, setCurrentIndex] = useState(0)

  const activeAlerts = useMemo(() => {
    if (!alerts) return []

    return alerts
      .filter(a => a.active)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
  }, [alerts])

  useEffect(() => {
    if (activeAlerts.length === 0 || !open) return
    markPageAlertsSeenInSession()
  }, [activeAlerts, open])

  const handleClose = () => {
    markPageAlertsSeenInSession()
    setOpen(false)
  }

  if (!open || activeAlerts.length === 0) return null

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % activeAlerts.length)
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + activeAlerts.length) % activeAlerts.length)
  }

  const currentAlert = activeAlerts[currentIndex]
  const resolvedButton = currentAlert.button ? resolveNavLink(currentAlert.button) : null
  const hasImage = currentAlert.asset?.type === 'IMAGE' && currentAlert.asset?.preview

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className={`bg-background text-foreground rounded-2xl shadow-xl flex flex-col relative gap-6 w-full ${hasImage ? 'max-w-4xl' : 'max-w-2xl'}`}>
        <div className="rounded-2xl flex flex-col md:flex-row w-full h-full relative min-h-[200px] overflow-hidden bg-secondary/5 border border-transparent">
          {/* Content */}
          <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
            <div className="flex flex-col gap-6 max-h-[50vh] md:max-h-[80vh] overflow-y-auto pr-2">
              <div
                className="prose prose-sm max-w-none prose-p:my-0 prose-headings:my-0 prose-h1:text-lg prose-h2:text-base prose-h3:text-sm"
                dangerouslySetInnerHTML={{ __html: currentAlert.content }}
              />
              {currentAlert.button && currentAlert.button.label && resolvedButton && (
                <div className="mt-2">
                  <Link
                    to={resolvedButton.url}
                    target={resolvedButton.isExternal ? '_blank' : undefined}
                    rel={resolvedButton.isExternal ? 'noopener noreferrer' : undefined}
                    className="inline-flex bg-primary text-on-primary rounded-lg px-6 py-2.5 font-semibold hover:bg-primary/80 transition-colors"
                  >
                    {currentAlert.button.label}
                  </Link>
                </div>
              )}
            </div>

            {/* Controls */}
            {activeAlerts.length > 1 && (
              <div className="flex items-center justify-between pt-6 mt-auto">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="p-2 rounded-full border border-primary/20 text-primary-text hover:bg-primary/10 transition-colors cursor-pointer"
                  title={t('alerts.previousAriaLabel')}
                  aria-label={t('alerts.previousAriaLabel')}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex gap-2">
                  {activeAlerts.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      title={t('alerts.goToAlertAriaLabel', { number: idx + 1 })}
                      aria-label={t('alerts.goToAlertAriaLabel', { number: idx + 1 })}
                      className={`w-2.5 h-2.5 rounded-full transition-colors cursor-pointer ${idx === currentIndex ? 'bg-primary' : 'bg-primary/20 hover:bg-primary/40'
                        }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleNext}
                  className="p-2 rounded-full border border-primary/20 text-primary-text hover:bg-primary/10 transition-colors cursor-pointer"
                  title={t('alerts.nextAriaLabel')}
                  aria-label={t('alerts.nextAriaLabel')}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Right: Image */}
          {hasImage && (
            <div className="flex-1 bg-secondary/10 flex items-center justify-center relative min-h-[200px] max-h-[30vh] md:max-h-none md:min-h-full">
              <img
                src={currentAlert.asset!.preview}
                alt={t('alerts.imageAlt')}
                className="object-cover w-full h-full absolute inset-0"
                width={1000}
                height={1000}
                loading="lazy"
              />
            </div>
          )}

          {/* Close button */}
          <button
            type="button"
            className="absolute top-4 right-4 bg-primary text-on-primary shadow-lg rounded-full p-1 flex items-center justify-center hover:bg-primary/80 transition-colors z-10 cursor-pointer"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}
            onClick={handleClose}
            title={t('alerts.closeAriaLabel')}
            aria-label={t('alerts.closeAriaLabel')}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  )
}
